import { useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useUpdateMatter } from '@/api/mutations/useUpdateMatter';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
import formatDate from '@/helpers/formatDate';
import { hasAnyOngoingRun } from '@/helpers/runs';
import { useHasPermission, PermissionGate } from '@/hooks/useHasPermission';
import { EditFieldDialogControlled } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { InfoCard } from '@/new/components/info-card';
import { Button } from '@/new/components/shadcn/button';
import { Label } from '@/new/components/shadcn/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { DeleteMatterDialog } from '@/new/pages/matters/components/delete-matter-dialog';

export function Metadata() {
  const { matterId } = useParams();
  if (!matterId) return null;

  return <MetadataContent matterId={matterId} />;
}

function MetadataContent({ matterId }: { matterId: string }) {
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const { data: matter } = useGetMatterV2(matterId);
  const { data: runs } = useGetMatterRuns(matterId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { mutateAsync: updateMatter } = useUpdateMatter(matterId);

  // Creator can always edit/delete their own matters
  const isCreator = matter?.createdById === user?.id;
  const canDelete = hasPermission(Permission.MatterDelete) || isCreator;

  // Check if there are any ongoing runs
  const hasOngoingRuns = hasAnyOngoingRun(runs);

  const onEditMatterName = async (value: string) => {
    const name = value.trim();
    if (!name) {
      toast.error('Matter name cannot be empty or just spaces.');
      throw new Error('Matter name cannot be empty or just spaces.');
    }

    trackCustomEvent(TrackingEventsV2.MatterEdit, { matterId });
    await updateMatter({ name });
  };

  const onEditMatterCode = async (value: string) => {
    trackCustomEvent(TrackingEventsV2.MatterEdit, { matterId });
    await updateMatter({ code: value.trim() || undefined });
  };

  if (!matter) return null;

  return (
    <>
      <SettingsHeader
        title="Matter Metadata"
        subheading={<p className="text-sm text-gray-500">View and update your matter name and metadata information.</p>}
      />

      <div className="mb-4 space-y-2">
        <Label className="text-xs font-medium text-gray-500">Matter Name</Label>
        <div className="flex items-center gap-1">
          <div className="text-sm">{matter.name}</div>
          <PermissionGate permission={Permission.MatterEdit} orAllowed={isCreator} uiMode="disabled">
            <Button
              variant="ghost"
              size="iconSm"
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setIsModalOpen(true)}
              aria-label="Edit matter name"
            >
              <Icon className="lucide--pencil" />
            </Button>
          </PermissionGate>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-medium text-gray-500">Matter Code</Label>
            <div className="flex items-center gap-1">
              <kbd className="font-mono text-sm">{matter.code || 'Not set'}</kbd>
              <PermissionGate permission={Permission.MatterEdit} orAllowed={isCreator} uiMode="disabled">
                <Button
                  variant="ghost"
                  size="iconSm"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setIsCodeModalOpen(true)}
                  aria-label="Edit matter code"
                >
                  <Icon className="lucide--pencil" />
                </Button>
              </PermissionGate>
            </div>
          </div>

          {matter.createdDate && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-500">Created</Label>
              <div className="flex items-center gap-2 text-sm">
                <Icon className="lucide--calendar h-4 w-4 text-gray-400" />
                <span>{formatDate(matter.createdDate, true, true)}</span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs font-medium text-gray-500">Created By</Label>
            <div className="flex items-center gap-2 text-sm">
              <span>{matter.createdBy?.email || 'Unknown'}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium text-gray-500">Matter ID</Label>
            <div className="font-mono text-sm">{matter.id}</div>
          </div>
        </div>

        {matter.description && (
          <div className="space-y-2">
            <Label className="text-xs font-medium text-gray-500">Description</Label>
            <div className="text-sm">{matter.description}</div>
          </div>
        )}
      </div>

      <div className="mt-10 border-t border-gray-200 pt-6 pb-12">
        <h3 className="mb-1 text-base font-medium text-gray-700">Delete Documents</h3>
        <p className="mb-4 text-sm text-gray-500">Deleting documents will also remove any associated work product.</p>

        <InfoCard className="-mx-0.5 mt-3 mb-4 w-fit gap-2.25 px-2.75 py-1.5 pt-1.75">
          <div className="-mt-px flex items-center gap-2 text-gray-500">
            <p>
              Go to <span className="text-gray-800">Documents</span>
            </p>
            <Icon className="lucide--chevron-right" />
            <span className="text-gray-800">Click</span>
            <span className="inline-block rounded-md border bg-white px-1 py-1 text-xs">
              <Icon className="lucide--more-vertical text-gray-700" />
            </span>
            <Icon className="lucide--chevron-right" />
            <span className="text-gray-800">Delete Documents</span>
          </div>
        </InfoCard>

        <h3 className="mb-1 text-base font-medium text-gray-700">Delete Matter</h3>

        <p className="mb-4 text-sm text-gray-500">
          Permanently delete this matter, all subsets, documents, and associated data.
        </p>

        {canDelete && !hasOngoingRuns ? (
          <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
            <Icon className="lucide--trash-2 h-3 w-3" />
            Delete Matter
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button variant="destructive" disabled>
                  <Icon className="lucide--trash-2 h-3 w-3" />
                  Delete Matter
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-56">
              <p className="text-center">
                {hasOngoingRuns
                  ? 'Cannot delete matter while processing is ongoing'
                  : 'Only the matter creator or users with admin permissions can delete this matter'}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <DeleteMatterDialog
        matterId={matterId}
        matterName={matter.name}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        redirectOnSuccess
      />

      <EditFieldDialogControlled
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        title="Update Matter Name"
        description="Update the name for this matter."
        fieldLabel="Matter Name"
        currentValue={matter.name}
        placeholder="Enter matter name..."
        onSave={onEditMatterName}
      />

      <EditFieldDialogControlled
        open={isCodeModalOpen}
        onOpenChange={setIsCodeModalOpen}
        title={matter.code ? 'Update Matter Code' : 'Add Matter Code'}
        description="Update the code for this matter."
        fieldLabel="Matter Code"
        currentValue={matter.code ?? ''}
        placeholder="Enter matter code..."
        onSave={onEditMatterCode}
      />
    </>
  );
}
